import { Link } from 'react-router-dom';
import type { MangaItem } from '@/mocks/manga';

interface MangaCardProps {
  manga: MangaItem;
  variant?: 'default' | 'compact' | 'featured';
}

export default function MangaCard({ manga, variant = 'default' }: MangaCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${manga.id}`}
        className="group flex gap-3 p-2 rounded-lg hover:bg-background-200/50 transition-colors cursor-pointer"
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={manga.cover}
            alt={manga.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors truncate">
            {manga.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">{manga.author}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs text-foreground-400">{manga.category}</span>
            <span className="text-xs text-accent-500">{manga.rating}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${manga.id}`}
        className="group relative block rounded-xl overflow-hidden cursor-pointer h-[320px]"
      >
        <img
          src={manga.cover}
          alt={manga.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-500/80 text-white whitespace-nowrap">{manga.category}</span>
            {manga.status === '连载中' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-500/80 text-white whitespace-nowrap">连载中</span>
            )}
          </div>
          <h3 className="text-lg font-heading font-bold text-white mb-1">{manga.title}</h3>
          <p className="text-sm text-white/70 line-clamp-1">{manga.author}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${manga.id}`}
      className="group block bg-background-100 rounded-xl overflow-hidden border border-background-200/50 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[3/4] overflow-hidden">
        <img
          src={manga.cover}
          alt={manga.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-200 group-hover:text-primary-500 transition-colors truncate">
          {manga.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 truncate">{manga.author}</p>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-500 whitespace-nowrap">
            {manga.category}
          </span>
          <div className="flex items-center gap-1 text-accent-500">
            <i className="ri-star-fill text-xs w-3 h-3 flex items-center justify-center"></i>
            <span className="text-xs font-medium">{manga.rating}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
            {(manga.views / 10000).toFixed(1)}万
          </span>
          <span>{manga.chapters}话</span>
        </div>
      </div>
    </Link>
  );
}